import {cn} from '@/lib/utils';
import {FC, PropsWithChildren} from 'react';

export type PillSize = 'xs' | 'sm' | 'md' | 'lg';

const sizeClasses: Record<PillSize, string> = {
  xs: 'text-xs px-2 py-0.5',
  sm: 'text-xs px-2.5 py-1',
  md: 'text-sm px-3 py-1.5',
  lg: 'text-base px-4 py-2',
};

export type PillVariant = 'default' | 'overlay' | 'ghost';

const variantClasses: Record<PillVariant, string> = {
  default: 'bg-secondary border-secondary-500 text-white',
  overlay:
    'bg-secondary/70 border-secondary-500 font-medium text-white backdrop-blur-lg',
  ghost: 'bg-transparent border-neutral-500 text-neutral-900',
};

/** Opacity (0–100) applied to a custom color's background per variant */
const variantColorOpacity: Record<PillVariant, number> = {
  default: 100,
  overlay: 70,
  ghost: 0,
};

interface PillProps {
  className?: string;
  size?: PillSize;
  variant?: PillVariant;
  /** Overrides the variant background and border with a specific CSS color value */
  color?: string;
}

export const Pill: FC<PropsWithChildren<PillProps>> = ({
  className,
  size = 'md',
  variant = 'default',
  color,
  children,
}) => {
  return (
    <div
      className={cn(
        'capitalize border font-medium rounded-full tracking-wide',
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
      style={
        color
          ? {
              backgroundColor: `color-mix(in srgb, ${color} ${variantColorOpacity[variant]}%, transparent)`,
              borderColor: color,
            }
          : undefined
      }
    >
      {children}
    </div>
  );
};
